import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import {
	Autocomplete,
	Box,
	Chip,
	Grid2,
	IconButton,
	InputAdornment,
	MenuItem,
	Select,
	Slider,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { rollPositionCodes } from "../RollPositionSelector";
import { rollStatusCodes } from "../RollStatusSelector";
import { optionsTypeCode } from "../RollTypeSelector";

const CustomTypography = ({ children }: { children: React.ReactNode }) => {
	return (
		<Typography
			sx={{
				fontSize: "0.8rem",
				color: (theme) => theme.palette.text.disabled,
				marginBottom: (theme) => theme.spacing(0.5),
			}}
		>
			{children}
		</Typography>
	);
};

export const OptionChip = ({
	label,
	selected,
	onClick,
}: {
	label: string;
	selected: boolean;
	onClick: () => void;
}) => {
	return (
		<Chip
			sx={{
				borderRadius: (theme) => theme.shape.borderRadius,
				fontWeight: selected ? 600 : 400,
				fontSize: "0.8rem",
				height: 30,
				transition: "all 0.15s ease",
				border: (theme) =>
					`1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
				backgroundColor: (theme) =>
					selected ? alpha(theme.palette.primary.main, 0.1) : "transparent",
				color: (theme) =>
					selected ? theme.palette.primary.main : theme.palette.text.secondary,
				"&:hover": {
					backgroundColor: (theme) =>
						selected
							? alpha(theme.palette.primary.main, 0.18)
							: alpha(theme.palette.text.primary, 0.05),
					borderColor: (theme) =>
						selected
							? theme.palette.primary.main
							: theme.palette.text.secondary,
				},
			}}
			label={label}
			onClick={onClick}
		/>
	);
};

const FilterChip = ({
	label,
	checked,
	onClick,
}: {
	label: string;
	checked: boolean;
	onClick: () => void;
}) => {
	return (
		<Chip
			sx={{
				fontWeight: checked ? 600 : 400,
				cursor: "pointer",
				height: 30,
				transition: "all 0.15s ease",
				border: (theme) =>
					`1.5px solid ${checked ? theme.palette.primary.main : theme.palette.divider}`,
				backgroundColor: (theme) =>
					checked ? alpha(theme.palette.primary.main, 0.1) : "transparent",
				color: (theme) =>
					checked ? theme.palette.primary.main : theme.palette.text.secondary,
				"&:hover": {
					backgroundColor: (theme) =>
						checked
							? alpha(theme.palette.primary.main, 0.18)
							: alpha(theme.palette.text.primary, 0.05),
				},
			}}
			label={label}
			onClick={onClick}
		/>
	);
};

export type SortByField = "compatibility" | "name" | "diameter" | "status";
export type SortDirection = "asc" | "desc";

export interface RollFiltersState {
	search: string;
	type: "U" | "E" | null;
	statuses: string[];
	position: "A" | "T" | "B" | null;
	diameterRange: [number, number] | null;
	inRollset: boolean;
	inCarrier: boolean;
	compatibleWithStand: boolean;
	compatibleWithSection: boolean;
	sortBy: SortByField;
	sortDirection: SortDirection;
}

export const INITIAL_ROLL_FILTERS: RollFiltersState = {
	search: "",
	type: null,
	statuses: rollStatusCodes.map((s) => s.Code),
	position: null,
	diameterRange: null,
	inRollset: false,
	inCarrier: false,
	compatibleWithStand: false,
	compatibleWithSection: false,
	sortBy: "compatibility",
	sortDirection: "asc",
};

const SORT_OPTIONS: { value: SortByField; label: string }[] = [
	{ value: "compatibility", label: "Compatibility" },
	{ value: "name", label: "Name" },
	{ value: "diameter", label: "Diameter" },
	{ value: "status", label: "Status" },
];

type Props = {
	filters: RollFiltersState;
	onChange: (filters: RollFiltersState) => void;
	diameterBounds?: { min: number; max: number };
	standId?: number;
	sectionId?: number;
};

export const RollFilters = ({
	filters,
	onChange,
	diameterBounds,
	standId,
	sectionId,
}: Props) => {
	const handleChange = (partial: Partial<RollFiltersState>) => {
		onChange({ ...filters, ...partial });
	};

	const selectedStatuses = rollStatusCodes.filter((s) =>
		filters.statuses.includes(s.Code),
	);

	return (
		<Box
			padding={1}
			sx={{
				backgroundColor: (theme) => theme.palette.background.paper,
			}}
			marginBottom={2}
		>
			<Grid2 container spacing={2} alignItems="center">
				<Grid2 size={{ xs: 12, md: 6 }}>
					<CustomTypography>Search</CustomTypography>
					<TextField
						placeholder="Roll name…"
						value={filters.search}
						onChange={(e) => handleChange({ search: e.target.value })}
						size="small"
						fullWidth
						margin="none"
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon
											fontSize="small"
											sx={{ color: "text.disabled" }}
										/>
									</InputAdornment>
								),
							},
							htmlInput: {
								"data-test-id": "roll-filters-search",
							},
						}}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<CustomTypography>Sort by</CustomTypography>
					<Box sx={{ display: "flex", gap: 0.5 }}>
						<Select
							value={filters.sortBy}
							onChange={(e) =>
								handleChange({ sortBy: e.target.value as SortByField })
							}
							size="small"
							fullWidth
							startAdornment={
								<InputAdornment position="start">
									<SortIcon fontSize="small" sx={{ color: "text.disabled" }} />
								</InputAdornment>
							}
						>
							{SORT_OPTIONS.map((o) => (
								<MenuItem key={o.value} value={o.value}>
									{o.label}
								</MenuItem>
							))}
						</Select>
						<Tooltip
							title={
								filters.sortDirection === "asc" ? "Ascending" : "Descending"
							}
						>
							<IconButton
								size="small"
								onClick={() =>
									handleChange({
										sortDirection:
											filters.sortDirection === "asc" ? "desc" : "asc",
									})
								}
								sx={{
									border: (theme) => `1px solid ${theme.palette.divider}`,
									borderRadius: 1,
								}}
							>
								{filters.sortDirection === "asc" ? (
									<ArrowUpwardIcon fontSize="small" />
								) : (
									<ArrowDownwardIcon fontSize="small" />
								)}
							</IconButton>
						</Tooltip>
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<CustomTypography>Status</CustomTypography>
					<Autocomplete
						multiple
						disableCloseOnSelect
						size="small"
						options={rollStatusCodes}
						getOptionLabel={(option) => option.Name}
						value={selectedStatuses}
						onChange={(_, newValue) =>
							handleChange({ statuses: newValue.map((s) => s.Code) })
						}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder={
									selectedStatuses.length == 0 ? "Select status" : ""
								}
							/>
						)}
						renderOption={(props, option) => (
							<Box
								{...props}
								component="li"
								sx={{ display: "flex", alignItems: "center", gap: 1 }}
							>
								<Box
									sx={{
										width: 10,
										height: 10,
										borderRadius: "50%",
										backgroundColor: option.Color,
										flexShrink: 0,
									}}
								/>
								{option.Name}
							</Box>
						)}
						renderTags={(value, getTagProps) =>
							value.length === rollStatusCodes.length ? (
								<Chip
									label="All"
									size="small"
									color="primary"
									onDelete={() => handleChange({ statuses: [] })}
								/>
							) : (
								value.map((option, index) => (
									<Chip
										{...getTagProps({ index })}
										key={option.Code}
										label={option.Name}
										size="small"
										sx={{
											backgroundColor: option.Color,
											color:
												option.Code === "R" || option.Code === "D"
													? "white"
													: "black",
											fontWeight: 600,
										}}
									/>
								))
							)
						}
					/>
				</Grid2>

				{diameterBounds && (
					<Grid2 size={{ xs: 12, md: 6 }}>
						<CustomTypography>Diameter</CustomTypography>
						<Box sx={{ px: 1 }}>
							<Slider
								value={
									filters.diameterRange ?? [
										diameterBounds.min,
										diameterBounds.max,
									]
								}
								onChange={(_, value) =>
									handleChange({ diameterRange: value as [number, number] })
								}
								valueLabelDisplay="auto"
								valueLabelFormat={(value) => value.toFixed(3)}
								min={diameterBounds.min}
								max={diameterBounds.max}
								step={0.5}
								disableSwap
								size="small"
							/>
						</Box>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								px: 1,
								mt: -0.5,
							}}
						>
							<Typography variant="caption" color="text.disabled">
								{(filters.diameterRange?.[0] ?? diameterBounds.min).toFixed(2)}
							</Typography>
							<Typography variant="caption" color="text.disabled">
								{(filters.diameterRange?.[1] ?? diameterBounds.max).toFixed(2)}
							</Typography>
						</Box>
					</Grid2>
				)}

				<Grid2 size={{ xs: 12, sm: 6, md: "auto" }}>
					<CustomTypography>Type</CustomTypography>
					<Box sx={{ display: "flex", gap: 0.75 }}>
						{optionsTypeCode.map((opt) => (
							<OptionChip
								key={opt.Code}
								label={opt.Name}
								selected={filters.type === opt.Code}
								onClick={() =>
									handleChange({
										type:
											filters.type === opt.Code
												? null
												: (opt.Code as "E" | "U"),
									})
								}
							/>
						))}
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12, sm: 6, md: "auto" }}>
					<CustomTypography>Position</CustomTypography>
					<Box sx={{ display: "flex", gap: 0.75 }}>
						{rollPositionCodes.map((opt) => (
							<OptionChip
								key={opt.Code}
								label={opt.Name}
								selected={filters.position === opt.Code}
								onClick={() =>
									handleChange({
										position:
											filters.position === opt.Code
												? null
												: (opt.Code as "A" | "T" | "B"),
									})
								}
							/>
						))}
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12, md: "auto" }} sx={{ ml: { md: "auto" } }}>
					<CustomTypography>Filters</CustomTypography>
					<Box display="flex" gap={0.5}>
						<FilterChip
							label="In Rollset"
							checked={filters.inRollset}
							onClick={() => handleChange({ inRollset: !filters.inRollset })}
						/>
						<FilterChip
							label="In Carrier"
							checked={filters.inCarrier}
							onClick={() => handleChange({ inCarrier: !filters.inCarrier })}
						/>
					</Box>
				</Grid2>
				{standId && sectionId ? (
					<Grid2 size={{ xs: 12, md: "auto" }} sx={{ ml: { md: "auto" } }}>
						<CustomTypography>Compatible with</CustomTypography>
						<Box display="flex" gap={0.5}>
							{standId && (
								<FilterChip
									label="Stand"
									checked={filters.compatibleWithStand}
									onClick={() =>
										handleChange({
											compatibleWithStand: !filters.compatibleWithStand,
										})
									}
								/>
							)}
							{sectionId && (
								<FilterChip
									label="Section"
									checked={filters.compatibleWithSection}
									onClick={() =>
										handleChange({
											compatibleWithSection: !filters.compatibleWithSection,
										})
									}
								/>
							)}
						</Box>
					</Grid2>
				) : null}
			</Grid2>
		</Box>
	);
};
